import { CircularProgress } from "@circle-react/components/shared/uikit/TipTap/AttachmentPreviews/CircularProgress";
import { Icon } from "@circleco/compass/components/Icon";

import { t } from "@/i18n-js/instance";

import { AttachmentBadge } from "./AttachmentBadge";
import { AttachmentPreview } from "./AttachmentPreview";
import type { AttachmentItem } from "./types";

interface AttachmentChipProps {
  attachment: AttachmentItem;
  onRemove?: (id: string) => void;
}

export const AttachmentChip = ({
  attachment,
  onRemove,
}: AttachmentChipProps) => {
  const { id, filename, status, progress, contentType } = attachment;
  const isUploading = status === "uploading";
  const hasError = status === "error";
  const shouldShowProgressRing = isUploading && progress !== undefined;
  const shouldShowRemoveButton = !!onRemove && !isUploading;

  return (
    <div
      className="group relative size-[68px] shrink-0 cursor-default"
      title={filename}
      data-attachment-chip
    >
      <div className="overflow-hidden rounded-md size-full bg-inverse/60">
        <AttachmentPreview attachment={attachment} />

        {isUploading && (
          <div
            className="absolute inset-0 rounded-md bg-inverse/30"
            aria-hidden="true"
          />
        )}

        {shouldShowProgressRing && (
          <div
            className="absolute bottom-1 left-1 rounded-full"
            aria-hidden="true"
          >
            <CircularProgress
              percentage={progress}
              size={20}
              strokeWidth={2}
              foregroundCircleClassName="text-inverse"
              backgroundCircleClassName="text-inverse"
            />
          </div>
        )}

        {hasError && (
          <div
            className="absolute inset-0 rounded-md bg-inverse/70"
            aria-hidden="true"
          />
        )}

        <div className="absolute inset-x-1 bottom-1 flex">
          <AttachmentBadge
            status={status}
            contentType={contentType}
            filename={filename}
          />
        </div>
      </div>

      {/* Using custom button here as IconButton does not provide size="xs". */}
      {/* Need to add that in @circleco/compass repo */}
      {shouldShowRemoveButton && (
        <div className="absolute z-10 -right-1 -top-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <button
            type="button"
            aria-label={t("circle_ai_home.attachments.remove_attachment")}
            onClick={() => onRemove?.(id)}
            className="size-auto p-1 bg-inverse rounded-md text-inverse"
          >
            <Icon name="cross" size="sm" width={10} height={10} />
          </button>
        </div>
      )}
    </div>
  );
};
